// withCreateReview HOC
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { generatePresignedUrl } from '../../../../../utils/commonSlice'
import { uploadImagesToS3Thunk } from '../../../../../utils/commonThunk'
import { useNavigate, useParams } from 'react-router-dom'
import {
	createProductReview,
	fetchDigitalProductDetails,
	fetchNonDigitalProductDetails,
	getUserProductReview,
	updateProductReview,
} from '../../LandingPage/Api/landingPageSlice'

export const withCreateReview = (WrappedComponent) => {
	const WithCreateReview = (props) => {
		const dispatch = useDispatch()
		const { productId } = useParams()
		const { isGame } = useParams()
		const { nonDigitalProductDetails, digitalProductDetails, productReview } = useSelector((state) => state.landingPage)

		const [formData, setFormData] = useState({
			review: '',
			productId,
			title: '',
			rating: 0,
			reviewImages: [],
			isGameProduct: isGame === 'true',
			reviewId: '',
		})
		const navigate = useNavigate()
		const [selectedImages, setSelectedImages] = useState([])

		useEffect(() => {
			if (productId) {
				const isGameProd = isGame === 'true'
				if (isGameProd) {
					dispatch(fetchDigitalProductDetails(productId))
				} else {
					dispatch(fetchNonDigitalProductDetails(productId))
				}
				const body = {
					productId,
				}
				dispatch(getUserProductReview(body))
			}
		}, [dispatch, productId])

		useEffect(() => {
			if (productReview) {
				setFormData((prev) => ({
					...prev,
					...productReview,
				}))
			}
		}, [productReview])

		const handleImageChange = useCallback(
			async (event) => {
				event.preventDefault()
				const files = event.target.files

				if (!files.length) return

				const validTypes = ['image/jpeg', 'image/png']
				const maxSizeInBytes = 100 * 1024 // 300 KB

				if (files.length > 4) {
					return toast.error('Maximum 4 images are allowed')
				}

				for (const file of files) {
					if (!validTypes.includes(file.type)) {
						toast.error('Only JPEG and PNG formats are allowed.')
						continue
					}

					if (file.size > maxSizeInBytes) {
						toast.error('File size exceeds 300 KB. Please upload a smaller file.')
						continue
					}

					const timestampedFileName = `${file.name.split('.')[0]}-${Date.now()}.${file.name.split('.').pop()}`
					const timestampedFile = new File([file], timestampedFileName, { type: file.type })

					try {
						const body = { fileNames: [timestampedFileName], fileType: 'reviews/' }
						const res = await dispatch(generatePresignedUrl(body))

						if (res?.payload?.s3Link?.[0]) {
							timestampedFile.presignedUrl = res.payload.s3Link[0].s3Link
							setSelectedImages((prev) => ({
								...prev,
								timestampedFile: [...(prev?.timestampedFile || []), timestampedFile],
							}))

							await handleImageUpload(timestampedFile)
						} else {
							console.error('Failed to generate presigned URL')
						}
					} catch (error) {
						console.error('Error in image handling:', error)
						toast.error('Failed to process the image. Please try again.')
					}
				}
			},
			[dispatch]
		)

		const handleImageUpload = useCallback(
			async (imageFile) => {
				if (imageFile && imageFile.presignedUrl) {
					try {
						const res = await dispatch(uploadImagesToS3Thunk(imageFile.presignedUrl, imageFile))
						if (res?.status === 200) {
							setFormData((prev) => ({ ...prev, reviewImages: [...(prev.reviewImages || []), imageFile.name] }))
						}
					} catch (error) {
						console.error('Error uploading image:', { error })
						toast.error('Failed to upload the image. Please try again.')
					} finally {
					}
				} else {
					console.error('Presigned URL for image is missing.')
				}
			},
			[dispatch]
		)

		const productReviewSubmit = async () => {
			let hasError = false

			if (formData.rating === 0) {
				toast.error('Please provide a rating.')
				hasError = true
			}
			if (!formData.title.trim()) {
				toast.error('Title cannot be empty.')
				hasError = true
			}
			if (!formData.review.trim()) {
				toast.error('Review cannot be empty.')
				hasError = true
			}

			if (hasError) return
			let res
			if (formData.reviewId) {
				res = await dispatch(updateProductReview(formData))
			} else {
				res = await dispatch(createProductReview(formData))
			}
			if (res?.payload?.data?.status !== 0) {
				navigate('/user/Orders')
			} else {
				toast.error(res?.payload?.data?.message)
			}
		}

		const handleChange = useCallback((e) => {
			const { name, value } = e.target
			setFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}, [])

		const handleRatingClick = useCallback((ratingValue) => {
			setFormData((prevData) => ({ ...prevData, rating: ratingValue }))
		}, [])

		return (
			<WrappedComponent
				{...props}
				productDetails={isGame === 'true' ? digitalProductDetails : nonDigitalProductDetails}
				formData={formData}
				handleImageChange={handleImageChange}
				productReviewSubmit={productReviewSubmit}
				selectedImage={selectedImages}
				handleChange={handleChange}
				handleRatingClick={handleRatingClick}
			/>
		)
	}

	return WithCreateReview
}
